import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import styles from './AdminLogin.module.css';
import './AdminLoginShell.css';
import { loginWithEmail, persistAuthSession, parseLoginError } from '../services/authService';

const nacLogoUrl = `${process.env.PUBLIC_URL || ''}/nac_logo_tight_circle.png`;
const retailImgUrl = `${process.env.PUBLIC_URL || ''}/landing/login_photo_admin.jpg`;

interface LoginForm {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noAutofillFocus, setNoAutofillFocus] = useState({ email: false, password: false });

  useEffect(() => {
    document.documentElement.classList.add('admin-login-active');
    return () => document.documentElement.classList.remove('admin-login-active');
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      const { token, user } = await loginWithEmail(
        formData.email,
        formData.password,
        'admin',
      );
      persistAuthSession(user, token);
      setIsLoading(false);
      navigate('/admin-portal/dashboard');
    } catch (error: unknown) {
      setIsLoading(false);
      setErrors({ general: parseLoginError(error, formData.email) });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement;
    const field = (target.dataset.field || target.name) as keyof LoginForm;
    const { value } = target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors] || errors.general) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FormErrors];
        delete next.general;
        return next;
      });
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <Link to="/" className={styles.brand}>
          <img src={nacLogoUrl} alt="NAC" className={styles.brandLogo} />
          <span>NAC Easy Solution</span>
        </Link>
        <span className={styles.portalTag}>Admin portal</span>
      </header>

      <main className={styles.main}>
        <section className={styles.formPanel} aria-label="Admin login">
          <p className={styles.badge}>
            <span className={styles.badgeDot} aria-hidden />
            Admin portal
          </p>
          <h1 className={styles.title}>Admin login</h1>
          <p className={styles.subtitle}>
            Sign in to manage your company and support your team.
          </p>

          <form
            noValidate
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            {errors.general && (
              <div className={styles.errorBanner}>
                <AlertCircle size={15} />
                <span>{errors.general}</span>
              </div>
            )}

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="admin-email">Email address</label>
              <input
                id="admin-email"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                type="text"
                name="login_email"
                data-field="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setNoAutofillFocus((p) => ({ ...p, email: true }))}
                placeholder="you@company.com"
                disabled={isLoading}
                readOnly={!noAutofillFocus.email}
                autoComplete="username"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-form-type="other"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="admin-password">Password</label>
              <div className={styles.passWrap}>
                <input
                  id="admin-password"
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  type={showPassword ? 'text' : 'password'}
                  name="login_password"
                  data-field="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setNoAutofillFocus((p) => ({ ...p, password: true }))}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  readOnly={!noAutofillFocus.password}
                  autoComplete="current-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                />
                <button
                  type="button"
                  className={styles.showBtn}
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
            </div>

            <div className={styles.actions}>
              <button type="submit" className={styles.submitBtn} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className={styles.spinner} />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>
          </form>

          <p className={styles.switchNote}>
            Signing in as staff?{' '}
            <button
              type="button"
              className={styles.switchLink}
              onClick={() => navigate('/user/login')}
              disabled={isLoading}
            >
              Staff login
            </button>
          </p>
        </section>

        <aside className={styles.visual} aria-hidden>
          <img src={retailImgUrl} alt="" className={styles.visualImg} />
        </aside>
      </main>

      <footer className={styles.footer}>
        ©{new Date().getFullYear()} - NAC Software and Tech Solutions. Made in Bhutan.
      </footer>
    </div>
  );
};

export default AdminLogin;
